import { useState, useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { Copy, Check, ExternalLink, Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import { useSystemsByPool } from '@/hooks/use-systems'
import { usePermissions } from '@/hooks/use-permissions'
import { apiService } from '@/services/apiService'
import { formatDateTime, formatPower, xenonSystemUrl } from '@/lib/utils'
import type { EVChargingStationConfiguration, ElectricVehicleProfile, EVStationMeasurement } from '@/services/api/evcsApi'
import type { components } from '../../../../../types/flex-api'

type System = components['schemas']['System']

type SortKey = 'systemID' | 'assetID' | 'plugState' | 'departure'
type SortDir = 'asc' | 'desc'

// Plug state sort order: most connected first
const PLUG_STATE_ORDER: Record<string, number> = {
  PLUGGED_ON_STATION_AND_PLUGGED_ON_VEHICLE: 0,
  PLUGGED_ON_STATION: 1,
  UNPLUGGED: 2,
}

const PLUG_STATE_LABELS: Record<string, string> = {
  UNPLUGGED: 'Unplugged',
  PLUGGED_ON_STATION: 'Plugged (station)',
  PLUGGED_ON_STATION_AND_PLUGGED_ON_VEHICLE: 'Plugged (vehicle)',
}

const PLUG_STATE_CLASS: Record<string, string> = {
  UNPLUGGED: 'text-muted-foreground',
  PLUGGED_ON_STATION: 'text-yellow-600 dark:text-yellow-400',
  PLUGGED_ON_STATION_AND_PLUGGED_ON_VEHICLE: 'text-green-600 dark:text-green-400',
}


function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }
  return (
    <button
      onClick={handleCopy}
      className="p-0.5 rounded hover:bg-muted transition-colors flex-shrink-0"
      title="Copy"
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
      )}
    </button>
  )
}

function SoCBar({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <span className="font-mono text-xs tabular-nums">{value.toFixed(0)} %</span>
    </div>
  )
}

function SortableHeader({
  label,
  sortKey,
  active,
  dir,
  onClick,
}: {
  label: string
  sortKey: SortKey
  active: boolean
  dir: SortDir
  onClick: (key: SortKey) => void
}) {
  return (
    <th
      className="px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
      onClick={() => onClick(sortKey)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-30" />
        )}
      </span>
    </th>
  )
}

function PlainHeader({ label }: { label: string }) {
  return (
    <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
      {label}
    </th>
  )
}

export function EVConfigView({ poolID }: { poolID: string }) {
  const { can } = usePermissions()
  const { systems, isLoading, isError, error } = useSystemsByPool(poolID)
  const [sortKey, setSortKey] = useState<SortKey>('systemID')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const systemsWithEVCS = useMemo(
    () =>
      (systems as System[]).filter((s) =>
        (s.assets ?? []).some((a) => a.type === 'EVSTATION')
      ),
    [systems]
  )

  // Flat list of all (systemIndex, system, asset) pairs — stable reference key for query ordering
  const assetPairs = useMemo(
    () =>
      systemsWithEVCS.flatMap((system, si) =>
        (system.assets ?? [])
          .filter((a) => a.type === 'EVSTATION')
          .map((asset) => ({ system, asset, si }))
      ),
    [systemsWithEVCS]
  )

  // ── Fan-out queries ────────────────────────────────────────────────────────

  const gatewayResults = useQueries({
    queries: systemsWithEVCS.map((s) => ({
      queryKey: ['system-gateways', s.systemID] as const,
      queryFn: async () => {
        const res = await apiService.evcs.getSystemWithGateways(s.systemID)
        if (!res.success) throw new Error(res.error ?? 'Failed to fetch gateways')
        return ((res.data as any)?.gateways?.[0]?.id as string | undefined) ?? null
      },
      staleTime: 5 * 60 * 1000,
    })),
  })

  const profilesResults = useQueries({
    queries: systemsWithEVCS.map((s) => ({
      queryKey: ['ev-profiles', s.systemID] as const,
      queryFn: async () => {
        const res = await apiService.evcs.listEVProfiles(s.systemID)
        if (!res.success) throw new Error(res.error ?? 'Failed to fetch profiles')
        return (res.data as ElectricVehicleProfile[]) ?? []
      },
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  })

  const liveResults = useQueries({
    queries: systemsWithEVCS.map((s) => ({
      queryKey: ['system-live', s.systemID] as const,
      queryFn: async () => {
        const res = await apiService.evcs.getSystemLiveMeasurements(s.systemID)
        if (!res.success) throw new Error(res.error ?? 'Failed to fetch live data')
        return ((res.data as any)?.evChargingStations ?? []) as EVStationMeasurement[]
      },
      staleTime: 0,
      refetchInterval: 30_000,
      retry: false,
    })),
  })

  // Config queries depend on gatewayID — enabled only once gateway resolves
  const configResults = useQueries({
    queries: assetPairs.map(({ si, asset }) => {
      const gatewayID = gatewayResults[si]?.data
      return {
        queryKey: ['ev-configuration', gatewayID, asset.id] as const,
        queryFn: async () => {
          const res = await apiService.evcs.getEVConfiguration(gatewayID!, asset.id)
          if (!res.success) throw new Error(res.error ?? 'Failed to fetch EV config')
          return res.data as EVChargingStationConfiguration
        },
        enabled: !!gatewayID,
        staleTime: 5 * 60 * 1000,
        retry: false,
      }
    }),
  })

  // ── Build flat resolved rows ───────────────────────────────────────────────

  const rows = useMemo(
    () =>
      assetPairs.map(({ system, asset, si }, i) => {
        const config = configResults[i]?.data
        const profiles = profilesResults[si]?.data
        const liveStation = liveResults[si]?.data?.find((s) => s.applianceID === asset.id)
        const assignedProfile = config?.evProfileID
          ? profiles?.find((p) => p.id === config.evProfileID)
          : undefined

        const gatewayLoading = gatewayResults[si]?.isLoading ?? false
        const configLoading = gatewayLoading || (configResults[i]?.isLoading ?? false)

        return {
          systemID: system.systemID,
          assetID: asset.id,
          plugState: liveStation?.plugState,
          currentSoC:
            liveStation?.stateOfCharge != null
              ? liveStation.stateOfCharge * 100
              : undefined,
          chargingPower: liveStation?.power,
          departure: config?.departureTimestamp,
          capacity: assignedProfile?.capacity,
          minRequestedSoC: assignedProfile?.minRequestedSoc,
          lastUpdated: config?.updatedAt,
          configLoading,
          configError: configResults[i]?.isError ?? false,
          liveLoading: liveResults[si]?.isLoading ?? false,
          profilesLoading: profilesResults[si]?.isLoading ?? false,
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetPairs, configResults, profilesResults, liveResults, gatewayResults]
  )

  // ── Sort ──────────────────────────────────────────────────────────────────

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'systemID':
          cmp = a.systemID.localeCompare(b.systemID)
          break
        case 'assetID':
          cmp = a.assetID.localeCompare(b.assetID)
          break
        case 'plugState': {
          const ao = PLUG_STATE_ORDER[a.plugState ?? ''] ?? 99
          const bo = PLUG_STATE_ORDER[b.plugState ?? ''] ?? 99
          cmp = ao - bo
          break
        }
        case 'departure':
          cmp = (a.departure ?? '').localeCompare(b.departure ?? '')
          break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [rows, sortKey, sortDir])

  // ── Guards ────────────────────────────────────────────────────────────────

  if (!can('AppliancesEVConfigurationRead')) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">
          You do not have permission to view EV configurations.
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading systems…
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-4">
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load systems.'}
        </p>
      </div>
    )
  }

  if (systemsWithEVCS.length === 0) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">No EVSTATION assets found in this pool.</p>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const sp = { sortKey, sortDir, onClick: handleSort }

  return (
    <div className="overflow-auto h-full">
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm">
          <tr className="border-b border-border">
            <SortableHeader label="System ID" sortKey="systemID" active={sortKey === 'systemID'} dir={sp.sortDir} onClick={sp.onClick} />
            <SortableHeader label="Asset ID" sortKey="assetID" active={sortKey === 'assetID'} dir={sp.sortDir} onClick={sp.onClick} />
            <SortableHeader label="Plug State" sortKey="plugState" active={sortKey === 'plugState'} dir={sp.sortDir} onClick={sp.onClick} />
            <PlainHeader label="Current SoC" />
            <PlainHeader label="Charging Power" />
            <SortableHeader label="Departure" sortKey="departure" active={sortKey === 'departure'} dir={sp.sortDir} onClick={sp.onClick} />
            <PlainHeader label="Capacity" />
            <PlainHeader label="Min Requested SoC" />
            <PlainHeader label="Last Updated" />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr
              key={`${row.systemID}-${row.assetID}`}
              className="border-b border-border/40 last:border-0 hover:bg-muted/30 transition-colors"
            >
              {/* System ID */}
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-xs">{row.systemID}</span>
                  <CopyButton text={row.systemID} />
                  <a
                    href={xenonSystemUrl(row.systemID)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-muted-foreground hover:text-foreground flex-shrink-0"
                    title="Open in XENON"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </td>

              {/* Asset ID */}
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-xs">{row.assetID}</span>
                  <CopyButton text={row.assetID} />
                </div>
              </td>

              {/* Plug State */}
              <td className="px-3 py-2.5">
                {row.liveLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                ) : row.plugState ? (
                  <span className={`text-xs font-medium ${PLUG_STATE_CLASS[row.plugState] ?? 'text-muted-foreground'}`}>
                    {PLUG_STATE_LABELS[row.plugState] ?? row.plugState}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </td>

              {/* Current SoC */}
              <td className="px-3 py-2.5">
                {row.liveLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                ) : row.currentSoC != null ? (
                  <SoCBar value={row.currentSoC} />
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </td>

              {/* Charging Power */}
              <td className="px-3 py-2.5 text-xs">
                {row.liveLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                ) : row.chargingPower != null ? (
                  <span className="font-mono tabular-nums">{formatPower(row.chargingPower)}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>

              {/* Departure */}
              <td className="px-3 py-2.5 text-xs">
                {row.configLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                ) : row.configError ? (
                  <span className="text-destructive">—</span>
                ) : row.departure ? (
                  formatDateTime(row.departure)
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>

              {/* Capacity */}
              <td className="px-3 py-2.5 text-xs">
                {row.profilesLoading || row.configLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                ) : row.capacity != null ? (
                  <span className="font-mono tabular-nums">{Math.round(row.capacity / 1000)} kWh</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>

              {/* Min Requested SoC */}
              <td className="px-3 py-2.5">
                {row.profilesLoading || row.configLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                ) : row.minRequestedSoC != null ? (
                  <SoCBar value={row.minRequestedSoC} />
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </td>

              {/* Last Updated */}
              <td className="px-3 py-2.5 text-xs text-muted-foreground">
                {row.configLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                ) : row.lastUpdated ? (
                  formatDateTime(row.lastUpdated)
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
